import React, { useState, useEffect } from 'react';
import { X, Copy, Sparkle, CheckCircle, Spinner, FilmSlate } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';

const CloneViralModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [viralPostText, setViralPostText] = useState('');
  const [platform, setPlatform] = useState('TikTok');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [user, setUser] = useState(null);

  // Re-fetch user data when modal opens to get latest profile settings
  useEffect(() => {
    if (isOpen) {
      const userData = getUser();
      setUser(userData);
      // Set platform from user profile (or default to TikTok)
      setPlatform(userData?.platform || 'TikTok');
    }
  }, [isOpen]);

  const platforms = ['TikTok', 'Instagram', 'YouTube Shorts', 'Facebook'];

  const handleClone = async () => {
    if (!viralPostText.trim() || viralPostText.length < 10) {
      toast.error('Please paste the viral post you want to clone');
      return;
    }

    setStep('generating');
    setLoading(true);

    try {
      const response = await api.post('/content/clone-viral', {
        viral_post_text: viralPostText,
        platform: platform,
        niche: user?.niche || 'online business'
      });

      setResult(response.data);
      setStep('result');
      if (onSuccess) onSuccess();
      toast.success('Viral post cloned successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone viral post');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setStep('input');
    setViralPostText('');
    setResult(null);
    onClose();
  };

  const handleNewClone = () => {
    setStep('input');
    setViralPostText('');
    setResult(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                <Copy size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Let Me Clone This</h2>
                <p className="text-sm text-slate-400">I'll adapt it for your niche</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Paste the viral post you want me to clone
                  </label>
                  <textarea
                    value={viralPostText}
                    onChange={(e) => setViralPostText(e.target.value)}
                    data-testid="clone-viral-input"
                    placeholder="Paste the viral post text here... (copy from TikTok, Instagram, etc.)"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                    rows={6}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    {viralPostText.length} characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Target Platform
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {platforms.map((p) => (
                      <button
                        key={p}
                        onClick={() => setPlatform(p)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          platform === p
                            ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400 border'
                            : 'bg-white/5 border-white/10 text-slate-400 border hover:bg-white/10'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                  <h4 className="text-emerald-400 font-medium mb-2">Here's What I'll Do</h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    <li>• Analyze what made this post go viral</li>
                    <li>• Extract the winning formula</li>
                    <li>• Rewrite it for your {user?.niche || 'niche'}</li>
                    <li>• Make it 100% original - no plagiarism</li>
                  </ul>
                </div>

                <button
                  onClick={handleClone}
                  data-testid="clone-submit-btn"
                  className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  <Sparkle size={20} weight="fill" />
                  <span>Clone It For Me</span>
                </button>
              </div>
            )}

            {/* Generating Step */}
            {step === 'generating' && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Spinner size={32} className="text-white animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">I'm Analyzing & Adapting</h3>
                <p className="text-slate-400">Creating an original version for your niche...</p>
              </div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <div className="space-y-6">
                {/* Video Coming Soon Banner */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-center">
                  <FilmSlate size={32} className="text-emerald-400 mx-auto mb-2" />
                  <p className="text-emerald-400 font-medium">Done! Here's your unique version</p>
                  <p className="text-slate-400 text-sm">Copy and use on {result.platform}</p>
                </div>

                {/* Quick Copy Actions */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => handleCopy(result.script, 'quick-script')}
                    data-testid="copy-cloned-script"
                    className={`p-3 rounded-xl text-center transition-all ${
                      copiedField === 'quick-script'
                        ? 'bg-emerald-500/20 border-emerald-500/50'
                        : 'bg-white/10 hover:bg-white/20 border-white/10'
                    } border`}
                  >
                    {copiedField === 'quick-script' ? (
                      <CheckCircle size={24} className="text-emerald-400 mx-auto mb-1" />
                    ) : (
                      <Copy size={24} className="text-white mx-auto mb-1" />
                    )}
                    <span className="text-xs text-white font-medium">Copy Script</span>
                  </button>
                  <button
                    onClick={() => handleCopy(result.caption, 'quick-caption')}
                    className={`p-3 rounded-xl text-center transition-all ${
                      copiedField === 'quick-caption'
                        ? 'bg-emerald-500/20 border-emerald-500/50'
                        : 'bg-white/10 hover:bg-white/20 border-white/10'
                    } border`}
                  >
                    {copiedField === 'quick-caption' ? (
                      <CheckCircle size={24} className="text-emerald-400 mx-auto mb-1" />
                    ) : (
                      <Copy size={24} className="text-white mx-auto mb-1" />
                    )}
                    <span className="text-xs text-white font-medium">Copy Caption</span>
                  </button>
                  <button
                    onClick={() => handleCopy(result.hashtags.map(t => `#${t}`).join(' '), 'quick-hashtags')}
                    className={`p-3 rounded-xl text-center transition-all ${
                      copiedField === 'quick-hashtags'
                        ? 'bg-emerald-500/20 border-emerald-500/50'
                        : 'bg-white/10 hover:bg-white/20 border-white/10'
                    } border`}
                  >
                    {copiedField === 'quick-hashtags' ? (
                      <CheckCircle size={24} className="text-emerald-400 mx-auto mb-1" />
                    ) : (
                      <Copy size={24} className="text-white mx-auto mb-1" />
                    )}
                    <span className="text-xs text-white font-medium">Copy Hashtags</span>
                  </button>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">New Title</label>
                    <button
                      onClick={() => handleCopy(result.title, 'title')}
                      className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'title' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-white text-lg font-semibold" data-testid="cloned-title">{result.title}</p>
                </div>

                {/* Hook */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">New Hook</label>
                    <button
                      onClick={() => handleCopy(result.hook, 'hook')}
                      className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'hook' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-emerald-400 font-medium bg-emerald-500/10 p-3 rounded-lg" data-testid="cloned-hook">
                    {result.hook}
                  </p>
                </div>

                {/* Script */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Rewritten Script</label>
                    <button
                      onClick={() => handleCopy(result.script, 'script')}
                      className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'script' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-white bg-white/5 p-4 rounded-lg leading-relaxed" data-testid="cloned-script">
                    {result.script}
                  </p>
                </div>

                {/* Caption */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Caption</label>
                    <button
                      onClick={() => handleCopy(result.caption, 'caption')}
                      className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'caption' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy</span>
                    </button>
                  </div>
                  <p className="text-slate-300 bg-white/5 p-4 rounded-lg" data-testid="cloned-caption">
                    {result.caption}
                  </p>
                </div>

                {/* Hashtags */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Hashtags</label>
                    <button
                      onClick={() => handleCopy(result.hashtags.map(t => `#${t}`).join(' '), 'hashtags')}
                      className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center space-x-1"
                    >
                      {copiedField === 'hashtags' ? <CheckCircle size={14} /> : <Copy size={14} />}
                      <span>Copy All</span>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="cloned-hashtags">
                    {result.hashtags.map((tag, index) => (
                      <span
                        key={index}
                        onClick={() => handleCopy(`#${tag}`, `tag-${index}`)}
                        className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm cursor-pointer hover:bg-emerald-500/30 transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleNewClone}
                    data-testid="clone-another-btn"
                    className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                  >
                    Clone Another
                  </button>
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-xl transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CloneViralModal;
